/** Guided self-test run panel (Phase 4 slice 2) — the run surface embedded
 * under the readiness tab's `self_test` section (AC1 + AC3). Renders a Run
 * button (gated on a declared suite + `workflows:configure`), live progress
 * while a run executes, and per-scenario verdicts in business language once
 * it lands.
 * Spec: docs/superpowers/specs/2026-08-18-sa109-guided-self-test-design.md §10
 */
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Badge, Button } from '@/components/ui'
import { useCanAccessFeature } from '@/hooks'
import { ApiError } from '@/services/apiClient'
import {
  useLatestSelfTestRun,
  useSelfTestRun,
  useStartSelfTest,
} from '@/hooks/useWorkflowSelfTest'
import type { SelfTestRun, SelfTestVerdict } from '@/types/workflowConfig'

const VERDICT_BADGE: Record<
  SelfTestVerdict['status'],
  { variant: 'success' | 'destructive' | 'warning'; label: string }
> = {
  passed: { variant: 'success', label: 'Passed' },
  failed: { variant: 'destructive', label: 'Failed' },
  error: { variant: 'warning', label: 'Error' },
}

function VerdictRow({ verdict }: { verdict: SelfTestVerdict }) {
  const { variant, label } = VERDICT_BADGE[verdict.status]
  return (
    <li className="text-sm">
      <div className="flex items-center gap-2">
        <Badge variant={variant}>{label}</Badge>
        <span className="font-medium text-foreground">{verdict.title}</span>
      </div>
      {verdict.failed_assertions.map((assertion, idx) => (
        <p key={idx} className="ml-1 mt-0.5 pl-6 text-xs text-muted-foreground">
          {assertion.detail}
        </p>
      ))}
      {verdict.error && (
        <p className="ml-1 mt-0.5 pl-6 text-xs text-destructive">{verdict.error}</p>
      )}
    </li>
  )
}

export function SelfTestPanel({
  workflowId,
  suiteDeclared,
}: {
  workflowId: string
  suiteDeclared: boolean
}) {
  const canRun = useCanAccessFeature('workflow_configuration', 'workflows:configure')
  const queryClient = useQueryClient()
  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  // Inline rather than the global Toast system on purpose — this panel would
  // otherwise be the only thing in the readiness tab's tree needing a
  // ToastProvider ancestor, which is a landmine for any other test that
  // mounts WorkflowOverviewPage/ReadinessTab without one.
  const [notice, setNotice] = useState<{ tone: 'info' | 'error'; message: string } | null>(null)

  // Carry-forward from Task 11's review: never poll `latest` AND
  // `runs/{id}` for the same running run — once a run id is known (either
  // adopted from `latest` below, or from a fresh POST/409), disable `latest`.
  const latest = useLatestSelfTestRun(workflowId, suiteDeclared && !activeRunId)
  const active = useSelfTestRun(workflowId, activeRunId)
  const start = useStartSelfTest(workflowId)

  // Resume-on-mount: adopt a still-`running` run discovered via `latest` so
  // it continues polling through the run-id hook instead.
  useEffect(() => {
    if (!activeRunId && latest.data && latest.data.status === 'running') {
      setActiveRunId(latest.data.id)
    }
  }, [activeRunId, latest.data])

  const run: SelfTestRun | null | undefined = activeRunId ? active.data : latest.data

  // Terminal transition → the readiness item (`self_test.last_run`) is stale
  // until refetched, and any 409-resume/start-failure notice is now about a
  // run that's no longer in flight — clear it so "resuming its progress"
  // can't sit above finished results.
  const prevStatus = useRef<SelfTestRun['status'] | undefined>(undefined)
  useEffect(() => {
    const status = active.data?.status
    if (prevStatus.current === 'running' && status && status !== 'running') {
      queryClient.invalidateQueries({ queryKey: ['workflows', 'readiness', workflowId] })
      setNotice(null)
    }
    prevStatus.current = status
  }, [active.data?.status, queryClient, workflowId])

  if (!suiteDeclared) {
    return null
  }

  const isRunning = run?.status === 'running'

  function handleRun() {
    setNotice(null)
    start.mutate(undefined, {
      onSuccess: (newRun) => {
        setActiveRunId(newRun.id)
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          const detail = err.detail as { code?: string; run_id?: string } | null
          if (detail?.run_id) {
            setNotice({
              tone: 'info',
              message: 'A self-test is already running — resuming its progress.',
            })
            setActiveRunId(detail.run_id)
            return
          }
        }
        setNotice({ tone: 'error', message: `Could not start self-test: ${err.message}` })
      },
    })
  }

  return (
    <div
      className="mt-2 space-y-2 rounded-md border border-border p-3"
      data-testid="self-test-panel"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Runs this template's built-in scenarios against the live workflow.
        </p>
        {canRun && (
          <Button size="sm" disabled={start.isPending || isRunning} onClick={handleRun}>
            {isRunning ? 'Running…' : 'Run self-test'}
          </Button>
        )}
      </div>

      {notice && (
        <p className={notice.tone === 'error' ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
          {notice.message}
        </p>
      )}

      {isRunning && (
        <p className="text-xs text-muted-foreground">
          {run?.verdicts?.length ?? 0} of {run?.scenario_total ?? 0} scenarios run
        </p>
      )}

      {!isRunning && run?.status === 'error' && (
        <div
          role="alert"
          className="rounded-sm border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
          <p className="font-medium">
            Self-test failed to complete: {run.error ?? 'An unexpected error stopped the run.'}
          </p>
        </div>
      )}

      {!isRunning && run?.status !== 'error' && run?.finished_at && (
        <p className="text-xs text-muted-foreground">
          Last run: {run.scenario_passed}/{run.scenario_total} passed
        </p>
      )}

      {!isRunning && run?.verdicts && run.verdicts.length > 0 && (
        <ul className="space-y-2">
          {run.verdicts.map((verdict) => (
            <VerdictRow key={verdict.scenario_id} verdict={verdict} />
          ))}
        </ul>
      )}
    </div>
  )
}
